import type { ReadinessResult } from '@unipostudio/shared-validators';
import { api } from './api';

/** Exactly the values Prisma's PostStatus enum can hold. */
export type PostStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'PUBLISHING'
  | 'PUBLISHED'
  | 'PARTIAL'
  | 'FAILED'
  | 'CANCELLED'
  | 'PENDING_APPROVAL';

/** Exactly the values Prisma's ResultStatus enum can hold. */
export type ResultStatus = 'PENDING' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'RETRYING';

/**
 * A post as `GET /posts` really sends it.
 *
 * This used to describe something else entirely — a `caption`, platforms as
 * `{ platform, postType }` objects, lowercase statuses and a `publishedAt` —
 * none of which the API has ever sent. Those were not blank fields but
 * crashes: `preview(post.caption)` calls `.length`, and the attention hook it
 * sits in is mounted in the sidebar and the mobile nav, so the app shell threw
 * as soon as a workspace had one post.
 *
 * Write payloads are still translated from the composer's own shape by
 * `toApiCreatePayload` below. Reads are not translated at all any more: the
 * type simply says what arrives.
 */
export interface Post {
  id: string;
  content: string;
  contentLang: string;
  /** The occasion the post was made for (PC-36), when it was. */
  occasionId?: string | null;
  status: PostStatus;
  /** Target platforms, SCREAMING_CASE, with the chosen type per platform beside them. */
  platforms: string[];
  postTypes: Record<string, string>;
  /** Per-channel extras beyond the caption (Google Business button, offer dates). */
  platformOptions?: Record<string, Record<string, string>>;
  scheduledAt: string | null;
  mediaKeys: string[];
  /** Displayable URLs for `mediaKeys`, in the same order. */
  mediaUrls?: string[];
  mediaTypes?: string[];
  publishedCount: number;
  failedCount: number;
  results?: PostResult[];
  /**
   * The pre-publish check for a post due within 26 hours (Task 1.2): ready,
   * at_risk or blocked, with each issue. Null until the worker has checked
   * it, and again after an edit until it checks it again.
   */
  readiness?: ReadinessResult | null;
  readinessCheckedAt?: string | null;
  /** When every channel had reported: the post's receipt is settled. */
  finalizedAt?: string | null;
  /** "Sara (via link)": who decided through an approval link (Task 5.1). */
  approverName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PostResult {
  id: string;
  platform: string;
  status: ResultStatus;
  platformPostId: string | null;
  platformPostUrl: string | null;
  errorMessage: string | null;
  /** The worker's failure code; known ones are shown translated (shared/lib/post-result-error). */
  errorCode?: string | null;
  attempts: number;
  publishedAt: string | null;
  /** The caption actually sent to this channel (the receipt's "What was posted"). */
  formattedContent?: string | null;
  /** Ten minutes after publishing: 'verified' (still there) or 'gone'; null when not checked. */
  verification?: 'verified' | 'gone' | null;
  verifiedAt?: string | null;
}

export interface CreatePostDto {
  caption: string;
  platforms: { platform: string; postType: string }[];
  mediaIds: string[];
  scheduledAt: string | null;
  publishNow: boolean;
  platformOptions?: Record<string, Record<string, string>>;
  /** Saved and sent for approval in one request: never scheduled before it is approved. */
  submitForApproval?: boolean;
  /** The occasion it is made for: the calendar counts it under that occasion (PC-36). */
  occasionId?: string;
}

/** Maps the frontend CreatePostDto to the backend API contract. */
function toApiCreatePayload(dto: CreatePostDto): Record<string, unknown> {
  const platforms = [...new Set(dto.platforms.map((p) => p.platform))];
  const postTypes: Record<string, string> = {};
  for (const p of dto.platforms) {
    postTypes[p.platform] = p.postType;
  }
  return {
    content: dto.caption,
    platforms,
    postTypes,
    mediaKeys: dto.mediaIds,
    scheduledAt: dto.scheduledAt ?? undefined,
    ...(dto.platformOptions ? { platformOptions: dto.platformOptions } : {}),
    ...(dto.submitForApproval ? { submitForApproval: true } : {}),
    ...(dto.occasionId ? { occasionId: dto.occasionId } : {}),
  };
}

/** Maps a partial frontend DTO to the backend API contract for updates. */
function toApiUpdatePayload(dto: Partial<CreatePostDto>): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (dto.caption !== undefined) payload.content = dto.caption;
  if (dto.platforms !== undefined) {
    payload.platforms = [...new Set(dto.platforms.map((p) => p.platform))];
    const postTypes: Record<string, string> = {};
    for (const p of dto.platforms) {
      postTypes[p.platform] = p.postType;
    }
    payload.postTypes = postTypes;
  }
  if (dto.mediaIds !== undefined) payload.mediaKeys = dto.mediaIds;
  if (dto.platformOptions !== undefined) payload.platformOptions = dto.platformOptions;
  // `null` must reach the API: it is how a draft is taken OFF the calendar
  // (the service maps null → scheduledAt = null). Only `undefined` means
  // "leave it alone".
  if (dto.scheduledAt !== undefined) payload.scheduledAt = dto.scheduledAt;
  if (dto.submitForApproval) payload.submitForApproval = true;
  return payload;
}

/**
 * The account's X post credits in the month a post would publish in
 * (GET /posts/x-credits). `used` leaves out the post being edited, whose own
 * reservation is `reservedForPost`; `limit` null is unlimited.
 */
export interface XCreditUsage {
  used: number;
  limit: number | null;
  resetsAt: string;
  reservedForPost: number;
}

export interface PostFilters {
  status?: string;
  platform?: string;
  search?: string;
  page?: number;
  limit?: number;
  /** ISO datetime — inclusive lower bound on scheduledAt (calendar range). */
  scheduledFrom?: string;
  /** ISO datetime — inclusive upper bound on scheduledAt. */
  scheduledTo?: string;
  /** Only posts with no scheduledAt (the calendar's drafts tray). */
  unscheduled?: boolean;
  /** Only posts waiting to publish with these pre-publish verdicts: 'at_risk,blocked'. */
  readiness?: string;
  /** 'gone': published posts a channel no longer has (the receipt's check). */
  verification?: 'gone';
}

/**
 * What GET /posts actually returns once the axios layer has unwrapped the
 * envelope: the counts sit beside the rows, not under a `pagination` key.
 */
interface BackendPostList {
  data?: Post[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

export const postsService = {
  async list(filters: PostFilters = {}): Promise<{ data: Post[]; pagination: { total: number; page: number; limit: number } }> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.platform) params.set('platforms', filters.platform);
    if (filters.search) params.set('search', filters.search);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.scheduledFrom) params.set('scheduledFrom', filters.scheduledFrom);
    if (filters.scheduledTo) params.set('scheduledTo', filters.scheduledTo);
    if (filters.unscheduled) params.set('unscheduled', 'true');
    if (filters.readiness) params.set('readiness', filters.readiness);
    if (filters.verification) params.set('verification', filters.verification);

    const res = await api.get<BackendPostList>(`/posts?${params.toString()}`);
    const body = res.data;
    const data = body?.data ?? [];
    return {
      data,
      pagination: {
        total: body?.total ?? data.length,
        page: body?.page ?? filters.page ?? 1,
        limit: body?.limit ?? filters.limit ?? data.length,
      },
    };
  },

  async getById(id: string): Promise<Post> {
    const res = await api.get(`/posts/${id}`);
    return res.data as Post;
  },

  /** Posts made for each occasion, by the id they were saved with (PC-36). */
  async occasionCounts(ids: readonly string[]): Promise<Record<string, number>> {
    if (ids.length === 0) return {};
    const res = await api.get<{ counts?: Record<string, number> }>('/posts/occasion-counts', { params: { ids: ids.join(',') } });
    return res.data?.counts ?? {};
  },

  async create(dto: CreatePostDto): Promise<Post> {
    const res = await api.post('/posts', toApiCreatePayload(dto));
    return res.data as Post;
  },

  async update(id: string, dto: Partial<CreatePostDto>): Promise<Post> {
    const res = await api.patch(`/posts/${id}`, toApiUpdatePayload(dto));
    return res.data as Post;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/posts/${id}`);
  },

  /** X post credits for the month of `at` (now when absent), `postId` left out of used. */
  async xCredits(at?: string | null, postId?: string | null): Promise<XCreditUsage> {
    const params = new URLSearchParams();
    if (at) params.set('at', at);
    if (postId) params.set('postId', postId);
    const query = params.toString();
    const res = await api.get<XCreditUsage>(`/posts/x-credits${query ? `?${query}` : ''}`);
    return res.data;
  },

  async publish(id: string): Promise<Post> {
    const res = await api.post(`/posts/${id}/publish`);
    return res.data as Post;
  },

  /** Approve a post that is awaiting approval; optionally publish it right away. */
  async approve(id: string, publishImmediately = false): Promise<Post> {
    const res = await api.post(`/posts/${id}/approve`, { publishImmediately });
    return res.data as Post;
  },

  async submitForApproval(id: string): Promise<Post> {
    const res = await api.post(`/posts/${id}/submit-for-approval`);
    return res.data as Post;
  },

  async deleteFromPlatforms(id: string): Promise<{ results: Array<{ platform: string; success: boolean; error?: string }> }> {
    const res = await api.delete(`/posts/${id}/platforms`);
    return res.data;
  },

  /** New text on the channels a post is live on; `contents` gives a channel its own (F35). */
  async editOnPlatforms(id: string, content: string, contents?: Record<string, string>): Promise<{ results: Array<{ platform: string; success: boolean; error?: string }> }> {
    const res = await api.patch(`/posts/${id}/platforms`, { content, ...(contents ? { contents } : {}) });
    return res.data;
  },
};
